import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../../shared/_TBComponents/Icon';
import Text from '../../../../../shared/_TBComponents/Text';
import NavigatorManageTable, { OverflowCell, nameSort } from './NavigatorManageTable';

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #FF0505;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #CC0404;
  }
`;

const ActionsCell = ({ row }) => {
  if (row.original.isDeleted) {
    return (
      <Text styledTextProps={{ fontSize: '14px', fontWeight: 700, color: '#FF0505' }}>
        DELETED
      </Text>
    );
  }

  return (
    <DeleteButton
      type='button'
      onClick={(e) => { e.stopPropagation(); row.original.onDelete(row.original); }}
    >
      <Icon icon='delete' size={14} color='#fff' />
      Delete
    </DeleteButton>
  );
};

ActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      isDeleted: PropTypes.bool,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const BlackoutDatesTable = ({ onDeleteBlackoutDate }) => {
  const blackoutDates = useSelector((state) => state.navigator.data.blackoutDates);
  const isLoadingBlackoutDates = useSelector((state) => state.navigator.ui.isLoadingBlackoutDates);

  const tableData = useMemo(
    () => blackoutDates.map((blackoutDate) => ({
      ...blackoutDate,
      onDelete: onDeleteBlackoutDate,
    })),
    [blackoutDates, onDeleteBlackoutDate],
  );

  const columns = useMemo(() => [
    {
      Header: 'Applies To',
      accessor: 'coachName',
      sortType: nameSort,
      headerStyle: { width: '180px' },
      cellStyle: { width: '180px' },
      Cell: OverflowCell,
    },
    {
      Header: 'Groups',
      accessor: 'groupName',
      sortType: nameSort,
      headerStyle: { minWidth: '180px', flex: '1' },
      cellStyle: { minWidth: '180px', flex: '1', overflow: 'hidden' },
      Cell: OverflowCell,
    },
    {
      Header: 'Start Date',
      accessor: 'dateStart',
      headerStyle: { width: '140px' },
      cellStyle: { width: '140px' },
      Cell: OverflowCell,
    },
    {
      Header: 'End Date',
      accessor: 'dateEnd',
      headerStyle: { width: '140px' },
      cellStyle: { width: '140px' },
      Cell: OverflowCell,
    },
    {
      Header: 'Reason',
      accessor: 'reason',
      disableSortBy: true,
      headerStyle: { minWidth: '200px', flex: '1' },
      cellStyle: { minWidth: '200px', flex: '1', overflow: 'hidden' },
      Cell: OverflowCell,
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '120px', textAlign: 'center' },
      cellStyle: { width: '120px', textAlign: 'center' },
      Cell: ActionsCell,
    },
  ], []);

  return (
    <NavigatorManageTable
      data={tableData}
      columns={columns}
      isLoading={isLoadingBlackoutDates}
      hideSearch
      emptyMessage='No blackout dates found.'
      entityLabel='blackout date'
      defaultSortBy={[{ id: 'dateStart', desc: false }]}
    />
  );
};

BlackoutDatesTable.propTypes = {
  onDeleteBlackoutDate: PropTypes.func,
};

BlackoutDatesTable.defaultProps = {
  onDeleteBlackoutDate: () => {},
};

export default BlackoutDatesTable;
